/**
 * AdminAbatimentoRequests — página admin para gerir solicitações de abatimento
 * que vendedores enviaram (querem usar comissão X pra abater compra Y).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeftRight, Check, X, Clock, FileText, DollarSign, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleString('pt-BR'); } catch { return s; } };

export default function AdminAbatimentoRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pendente');

  const carregar = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await axios.get(`${API}/admin/abatimento-requests`, { params });
      setRequests(r.data || []);
    } catch (e) { toast.error('Erro ao carregar'); }
    setLoading(false);
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (id) => {
    const motivo = window.prompt('Observação (opcional):', '');
    if (motivo === null) return;
    try {
      await axios.post(`${API}/admin/abatimento-requests/${id}/approve`, { motivo });
      toast.success('Abatimento aprovado e aplicado');
      carregar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao aprovar'); }
  };

  const reject = async (id) => {
    const motivo = window.prompt('Motivo da rejeição:', '');
    if (!motivo) return;
    try {
      await axios.post(`${API}/admin/abatimento-requests/${id}/reject`, { motivo });
      toast.success('Solicitação rejeitada');
      carregar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao rejeitar'); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <ArrowLeftRight className="w-6 h-6 text-amber-600" />
          Solicitações de Abatimento
        </h1>
        <p className="text-sm text-muted-foreground">Vendedores pedem para usar comissão própria abatendo o valor de uma compra deles na loja.</p>
      </div>

      <div className="flex gap-2" data-testid="abatimento-filter-tabs">
        {[
          { v: 'pendente', label: 'Pendentes' },
          { v: 'aprovado', label: 'Aprovados' },
          { v: 'rejeitado', label: 'Rejeitados' },
          { v: '', label: 'Todos' },
        ].map(o => (
          <button key={o.v} onClick={() => setFilter(o.v)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${filter === o.v ? 'bg-amber-600 text-white' : 'bg-card border border-border'}`}
            data-testid={`abatimento-filter-${o.v || 'todos'}`}>
            {o.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-sm text-muted-foreground py-8">Carregando...</p>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border rounded-xl" data-testid="abatimento-empty">
          <ArrowLeftRight className="w-10 h-10 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-muted-foreground">Nenhuma solicitação {filter ? `com status "${filter}"` : ''}.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {requests.map(r => (
            <div key={r.id} className="bg-card border border-border rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`abatimento-row-${r.id}`}>
              <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Field icon={User} label="Vendedor" value={r.vendor_name} />
                <Field icon={FileText} label="Romaneio" value={`#${r.romaneio_compra_numero || '-'}`} />
                <Field icon={DollarSign} label="Valor" value={fmtBRL(r.valor)} bold />
                <Field icon={Clock} label="Solicitado" value={fmtDate(r.created_at)} />
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={r.status} />
                {r.status === 'pendente' && (
                  <>
                    <button onClick={() => approve(r.id)} className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1" data-testid={`abatimento-approve-${r.id}`}>
                      <Check className="w-3.5 h-3.5" /> Aprovar
                    </button>
                    <button onClick={() => reject(r.id)} className="px-2.5 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1" data-testid={`abatimento-reject-${r.id}`}>
                      <X className="w-3.5 h-3.5" /> Rejeitar
                    </button>
                  </>
                )}
              </div>
              {r.motivo && <p className="md:col-span-4 text-xs italic text-muted-foreground border-l-2 border-amber-400 pl-2">&quot;{r.motivo}&quot;</p>}
              {r.decided_motivo && <p className="md:col-span-4 text-xs italic text-muted-foreground">Resposta admin: &quot;{r.decided_motivo}&quot;</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = {
    pendente: 'bg-amber-100 text-amber-700',
    aprovado: 'bg-emerald-100 text-emerald-700',
    rejeitado: 'bg-red-100 text-red-700',
  };
  const labels = { pendente: '⏳ Pendente', aprovado: '✓ Aprovado', rejeitado: '✗ Rejeitado' };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${map[status] || 'bg-gray-100'}`}>{labels[status] || status}</span>;
};

const Field = ({ icon: Icon, label, value, bold }) => (
  <div className="min-w-0">
    <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p>
    <p className={`text-sm truncate ${bold ? 'font-bold text-amber-700' : ''}`}>{value || '-'}</p>
  </div>
);
